import { Upload as UploadAntd } from 'antd'
import { RcFile, UploadProps as UploadAntdProps } from 'antd/lib/upload'
import axios, { AxiosError } from 'axios'
import { FormikHelpers } from 'formik'
import dayjs from 'dayjs'
import ButtonLink from '../button/Button'
import { axiosDefaultConfig } from 'constants/axios'
import { Configuration, MediaApi } from 'generated/api'

const axiosConfig = new Configuration(axiosDefaultConfig)
const mediaApi = new MediaApi(axiosConfig)

interface UploadProps<T> extends UploadAntdProps {
  disabled: boolean
  labelButton: string
  value: string
  placeholder: string
  accept: string
  helpers: FormikHelpers<T>
  formKey: string
  operationType: string
  pathFileName: string
  sourceUrl: 'filePath' | 'publicUrl'
}

export default function Upload<T extends NonNullable<unknown>>(props: UploadProps<T>) {
  const uploadMedia = async (file: File | Blob | string) => {
    const date = dayjs().toDate().valueOf()
    const response = await mediaApi.mediaPresignedUrl({
      operationType: props.operationType,
      mimeType: (file as File).type,
      originalFileName: `${props.pathFileName}/${date}`,
      fileSize: (file as File).size,
    })

    await axios.put(response.data.presignedUrl, file, {
      headers: {
        'Content-Type': (file as File).type,
      },
    })

    return response.data
  }

  const uploadProps: UploadAntdProps = {
    accept: props.accept,
    showUploadList: true,
    maxCount: 1,
    multiple: false,
    beforeUpload: async (file: RcFile) => {
      const maxFileSize = 2 * 1024 * 1024 // 2MB
      const minFileSize = 1024
      const isSizeMinAllowed = minFileSize < file.size
      const isSizeMaxAllowed = maxFileSize > file.size

      if (!isSizeMinAllowed) {
        await props.helpers.setFieldTouched(props.formKey, true)
        props.helpers.setFieldError(props.formKey, `${file.name} minimal ukuran lebih dari 1KB`)
        return false
      }

      if (!isSizeMaxAllowed) {
        await props.helpers.setFieldTouched(props.formKey, true)
        props.helpers.setFieldError(props.formKey, `${file.name} melebihi ukuran maksimal 2MB`)
        return false
      }

      return (isSizeMaxAllowed && isSizeMinAllowed) || UploadAntd.LIST_IGNORE
    },
    onChange: async info => {
      const { status } = info.file
      if (status === 'done') {
        await props.helpers.setFieldValue(props.formKey, info.file.response[props.sourceUrl])
      } else if (status === 'error') {
        await props.helpers.setFieldValue(props.formKey, '')
        props.helpers.setFieldError(props.formKey, `${info.file.name} gagal diupload, coba lagi`)
      } else if (status === 'removed') {
        await props.helpers.setFieldValue(props.formKey, '')
      }
    },
    customRequest: async ({ onError, onSuccess, file }) => {
      try {
        if (!uploadMedia) throw new Error('uploadMediaImage is not defined')
        const response = await uploadMedia(file)
        onSuccess?.(response)
      } catch (err) {
        const error = err as AxiosError
        onError?.(error)
      }
    },
  }

  return (
    <UploadAntd {...uploadProps} disabled={props.disabled}>
      <div className="flex">
        <ButtonLink
          label={props.labelButton}
          className="bg-[#3C5337] px-6 py-4 text-white rounded-r-none"
          variant="solid-primary"
        />
        <div className="w-80 bg-white px-4 py-3 text-[#858C95] whitespace-nowrap border-solid border-l-0 border-t-2 border-r-2 border-b-2 border-[#E5E5E7] rounded-r-lg">
          <p className="text-ellipsis overflow-hidden m-0">
            {props.value ? props.value : props.placeholder}
          </p>
        </div>
      </div>
    </UploadAntd>
  )
}

Upload.defaultProps = {
  disabled: false,
  labelButton: 'Pilih File',
  placeholder: 'Masukan File',
  accept: 'image/png, image/jpeg, image/jpg',
  sourceUrl: 'publicUrl',
}
